import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Container,
  Row,
  Col,
  ButtonToolbar,
  Collapse,
  Alert,
} from 'reactstrap';
import format from 'date-fns/format';
import MaterialTable from '../../../../shared/components/material-table';
import ColumnsActExamCorrectionDetail from './ColumnsActExamCorrectionDetail';
import SmartCard from '../../../../shared/components/SmartCard';
import {
  ACTIVE_TAB,
  COMMUNICATION_TYPE,
  EXAM_EVALUATION_TYPE,
  STATUS_TYPE,
} from '../../../../shared/utils/constants';
import DynamicNavigationTabsHidden from '../../../../shared/components/DynamicNavigationTabsHidden';
import EvaluationCorrectionForm from './ActivityOrEvaluationCorrectionForm';
import FullScreenDialog from '../../../../shared/components/FullScreenDialog';
import DialogAlertForFullScreenDialog from '../../../../shared/components/DialogAlertForFullScreenDialog';
import {
  examCorrectionListByExamId,
  taskCorrectionListByFilter,
  examCorrectionSave,
  taskCorrectionSave,
  taskEnd,
  examEnd,
} from '../../../../shared/queries/Communication';
import GraphQL from '../../../../GraphQL';
import { onShowAlert } from '../../../../shared/utils/smartutils';
import SmartButton from '../../../../shared/components/SmartButton';
import { showNotificationAutoDestroy } from '../../../../shared/components/ShowNotification';

const ActivityOrEvaluationCorrection = (props) => {
  const {
    t,
    dispatch,
    dto,
    toggle,
    isOpen,
    showLoading,
    hideLoading,
    onFilterClickHandler,
  } = props;
  const [dialogAlert, setDialogAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(ACTIVE_TAB.ONE);
  const [correction, setCorrection] = useState({});
  const [notificationAlert, setNotificationAlert] = useState({});
  const [teacher, setTeacher] = useState('');
  const [subject, setSubject] = useState('');
  const [dataForCorrection, setDataForCorrection] = useState([]);
  const [rangeGrade, setRangeGrade] = useState({
    minGrade: 0,
    maxGrade: 0,
  });

  const toggleTab = (index) => {
    setActiveTab(index);
  };

  const toggleLoading = (value) => {
    setLoading(value);
  };

  const toggleDialog = () => {
    setDialogAlert(!dialogAlert);
  };

  const selectedCorrection = (evt, rowData) => {
    setCorrection(rowData);
    toggleTab(ACTIVE_TAB.TWO);
  };

  const formatStatus = (status) => {
    let type;
    switch (status) {
      case STATUS_TYPE.CONCLUDED:
        type = STATUS_TYPE.CONCLUDED;
        break;
      case STATUS_TYPE.IN_REVISION:
        type = STATUS_TYPE.IN_REVISION;
        break;
      case STATUS_TYPE.UN_CONCLUDED:
        type = STATUS_TYPE.UN_CONCLUDED;
        break;
      case STATUS_TYPE.ANSWERED:
        type = STATUS_TYPE.ANSWERED;
        break;
      case STATUS_TYPE.PENDING:
        type = STATUS_TYPE.PENDING;
        break;
      default:
    }
    return t(`communication.activityDetails.activityStatus.${type}`);
  };

  const formatEvaluationType = (value) => {
    let type;
    switch (value) {
      case EXAM_EVALUATION_TYPE.SUM:
        type = t(`communication.evaluation-type.${EXAM_EVALUATION_TYPE.SUM}`);
        break;
      case EXAM_EVALUATION_TYPE.AVG:
        type = t(`communication.evaluation-type.${EXAM_EVALUATION_TYPE.AVG}`);
        break;
      case EXAM_EVALUATION_TYPE.CONCEPT:
        type = t(
          `communication.evaluation-type.${EXAM_EVALUATION_TYPE.CONCEPT}`
        );
        break;
      default:
        break;
    }
    return type;
  };

  const loadTasksForCorrection = (id) => {
    toggleLoading(true);
    const query = taskCorrectionListByFilter(id);
    GraphQL(query, {})
      .then((res) => {
        toggleLoading(false);
        res.taskCorrectionListByFilter = res.taskCorrectionListByFilter.map(
          (item) => ({
            ...item,
            formattedStatus: formatStatus(item.status),
            formattedDeliveryDate: item.deliveryDate
              ? format(new Date(item.deliveryDate), 'dd/MM/yyyy')
              : null,
          })
        );
        setDataForCorrection(res.taskCorrectionListByFilter);
      })
      .catch(() => {
        toggleLoading(false);
        onShowAlert(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading'),
          setNotificationAlert
        );
      });
  };

  const loadExamsForCorrection = (id) => {
    toggleLoading(true);
    const query = examCorrectionListByExamId(id);
    GraphQL(query, {})
      .then((res) => {
        toggleLoading(false);
        res.examCorrectionListByFilter = res.examCorrectionListByFilter.map(
          (item) => ({
            ...item,
            formattedStatus: formatStatus(item.status),
          })
        );
        setDataForCorrection(res.examCorrectionListByFilter);
      })
      .catch(() => {
        toggleLoading(false);
        onShowAlert(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading'),
          setNotificationAlert
        );
      });
  };

  const endActivityOrExam = () => {
    let mutation;
    if (dialogAlert) {
      toggleDialog();
    }
    if (dto.type === COMMUNICATION_TYPE.ACTIVITY) {
      mutation = taskEnd(dataForCorrection);
    } else if (dto.type === COMMUNICATION_TYPE.EXAM) {
      mutation = examEnd(dataForCorrection);
    }
    toggle();
    showLoading();
    GraphQL(mutation, {})
      .then(() => {
        hideLoading();
        onFilterClickHandler();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-save'),
          setNotificationAlert
        );
      });
  };

  const loadData = () => {
    if (dto.type === COMMUNICATION_TYPE.ACTIVITY) {
      loadTasksForCorrection(dto.id);
      setTeacher(dto.taskHeader.teacher.name);
      setSubject(dto.taskHeader.subject.name);
    } else if (dto.type === COMMUNICATION_TYPE.EXAM) {
      loadExamsForCorrection(dto.id);
      setTeacher(dto.examHeader.teacher.name);
      setSubject(dto.examHeader.subject.name);
      const updatedRangeGrade = {
        minGrade: dto.examHeader.courseStep.course.minNote,
        maxGrade: dto.examHeader.courseStep.course.maxNote,
      };
      setRangeGrade(updatedRangeGrade);
    }
  };

  const showDialogAlert = () => {
    return (
      <DialogAlertForFullScreenDialog
        confirm={endActivityOrExam}
        message={
          dto.type === COMMUNICATION_TYPE.ACTIVITY
            ? t('notifications.warning-task-ending')
            : t('notifications.warning-exam-ending')
        }
        open={dialogAlert}
        toggle={toggleDialog}
      />
    );
  };

  const saveCorrection = (values) => {
    let mutation;
    if (values.type === COMMUNICATION_TYPE.ACTIVITY) {
      mutation = taskCorrectionSave(
        values.status,
        values.privateObs,
        values.publicObs,
        correction
      );
    } else if (values.type === COMMUNICATION_TYPE.EXAM) {
      mutation = examCorrectionSave(
        values.status,
        values.grade,
        values.privateObs,
        values.publicObs,
        correction
      );
    }
    GraphQL(mutation, {})
      .then(() => {
        toggleTab(ACTIVE_TAB.ONE);
        loadData();
        onShowAlert(
          'success',
          'success.title',
          'success.successfully-saved',
          setNotificationAlert
        );
      })
      .catch(() => {
        toggle();
        onShowAlert(
          'danger',
          'errors.title',
          'errors.error-to-save',
          setNotificationAlert
        );
      });
  };

  useEffect(() => {
    loadData();
  }, [dto]);

  return (
    <FullScreenDialog
      isOpen={isOpen}
      toggleDialog={toggle}
      toggleBackDialog={toggle}
      title={
        activeTab === ACTIVE_TAB.ONE
          ? `${t(`communication.activityDetails.result.${dto.type}`)} ${
              dto.id
            }  ${dto.title ? ` - ${dto.title}` : ''}`
          : `${t('communication.activityDetails.correctionObs')}: ${
              correction.student.fullName
            }`
      }
    >
      <DynamicNavigationTabsHidden activeTab={activeTab}>
        <SmartCard>
          <Collapse isOpen={notificationAlert.status} className="width100">
            <Alert color={notificationAlert.color}>
              {t(notificationAlert.message)}
            </Alert>
          </Collapse>
          <Container>
            {showDialogAlert()}
            <Row>
              <Col md={4} lg={4}>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center mb-2">
                    <span className="bold-text">{t('general.discipline')}</span>
                    <span className="pl-2">{subject}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2 pr-5">
                    <span className="bold-text">
                      {t('general.applicationDate')}
                    </span>
                    <span className="pl-2">
                      {format(new Date(dto.dueDateMili), 'dd/MM/yyyy')}
                    </span>
                  </div>
                  <div className="d-flex align-items-center pr-5 mb-2">
                    <span className="bold-text">{t('general.teacher')}</span>
                    <span className="pl-2">{teacher}</span>
                  </div>
                  {dto.type === COMMUNICATION_TYPE.EXAM && (
                    <>
                      <div className="d-flex align-items-center pr-5 mb-2">
                        <span className="bold-text">
                          {t('communication.evaluationWay')}
                        </span>
                        <span className="pl-2">
                          {dto.examHeader.evaluationWay.name}
                        </span>
                      </div>
                      <div className="d-flex align-items-center pr-5 mb-2">
                        <span className="bold-text">{t('general.type')}</span>
                        <span className="pl-2">
                          {formatEvaluationType(dto.examHeader.evaluationType)}
                        </span>
                      </div>
                      {dto.examHeader.weight && (
                        <div className="d-flex align-items-center pr-5 mb-2">
                          <span className="bold-text">
                            {t('general.weight')}
                          </span>
                          <span className="pl-2">{dto.examHeader.weight}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Col>
              <Col md={8} lg={8}>
                <span className="bold-text mb-2">{t('general.content')}</span>
                <div
                  className={`info-content-table-header ${
                    dto.type === COMMUNICATION_TYPE.ACTIVITY && 'short'
                  }`}
                >
                  {dto.htmlContent.replace(/<.*?>/g, '')}
                </div>
              </Col>
            </Row>
            <Row className="Table">
              <Col md={12} lg={12}>
                <div className="material-table__wrap">
                  <MaterialTable
                    isLoading={loading}
                    memoType={COMMUNICATION_TYPE.EXAM}
                    onRefreshClick={loadData}
                    data={dataForCorrection}
                    columns={ColumnsActExamCorrectionDetail(t, dto.type)}
                    paging={false}
                    onActivityCorrectionClick={selectedCorrection}
                    maxBodyHeightAuto
                  />
                </div>
              </Col>
            </Row>
            <ButtonToolbar>
              <SmartButton
                label={
                  dto.type === COMMUNICATION_TYPE.ACTIVITY
                    ? t('communication.activityDetails.endActivity')
                    : t('communication.activityDetails.endEvaluation')
                }
                disabled={!dto.editable}
                color="danger"
                onClick={() => {
                  toggleDialog();
                }}
              />
            </ButtonToolbar>
          </Container>
        </SmartCard>
        <EvaluationCorrectionForm
          onSubmit={saveCorrection}
          toggleTab={() => toggleTab(ACTIVE_TAB.ONE)}
          correction={correction}
          editable={!dto.editable}
          dispatch={dispatch}
          type={dto.type}
          dto={dto}
          saveCorrection={saveCorrection}
          rangeGrade={rangeGrade}
        />
      </DynamicNavigationTabsHidden>
    </FullScreenDialog>
  );
};
ActivityOrEvaluationCorrection.propTypes = {
  t: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  dispatch: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  onFilterClickHandler: PropTypes.func.isRequired,
};
export default withTranslation('common')(ActivityOrEvaluationCorrection);
